// © Thibault Sabathier, 2025. Tous droits réservés.
// utils/recruiterApiClient.js
//
// Axios wrapper DÉDIÉ au module Recruteur (commit 7a).
// Distinct de `apiClient.js` (member) car :
//   - L'auth recruteur utilise un cookie httpOnly SÉPARÉ (`recruiter_auth_token`,
//     posé par le backend lors du login recruteur).
//   - Les redirections 401 doivent pointer vers `/recruteurs/login` (et non
//     vers `/login` member).
//   - Le 404 `RECRUITER_MODULE_DISABLED` doit laisser passer l'erreur au
//     composant (pour afficher la page `<RecruiterModuleDisabled />`), PAS
//     rediriger.
//
// Convention :
//   - withCredentials: true (envoie le cookie recruiter_auth_token)
//   - baseURL alignée avec apiClient.js existant (centraux vs subdomain vs
//     custom domain) — le backend est commun, seul le cookie diffère.
//   - Pas de refresh token V1 : le JWT recruteur expire à 7j, l'utilisateur
//     se re-login. À ajouter en V2 si UX le justifie.

import axios from 'axios'
import { toTypedHttpError } from './httpErrors'

// --------------------------------------------
// DÉTECTION BASE URL (alignée avec apiClient.js member)
// --------------------------------------------
let API_BASE_URL

const hostname = window.location.hostname

const CENTRAL_DOMAINS = ['terrilink.io', 'www.terrilink.io', 'app.terrilink.io']
const isDev = hostname === 'localhost' || hostname === '127.0.0.1'
const isCentralDomain = CENTRAL_DOMAINS.includes(hostname)

if (isDev) {
  API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
} else if (isCentralDomain) {
  API_BASE_URL = 'https://api.terrilink.io/api'
} else {
  // Tenant subdomain ({slug}.terrilink.io) ou domaine custom
  // (app.aveyronworldwide.com, etc.) → reverse proxy local Nginx
  API_BASE_URL = `${window.location.origin}/api`
}

// --------------------------------------------
// AXIOS INSTANCE
// --------------------------------------------
const recruiterAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // ✅ Envoie cookie recruiter_auth_token httpOnly
})

// --------------------------------------------
// REQUEST INTERCEPTOR
// --------------------------------------------
recruiterAxios.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {}
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    } else {
      config.headers['Content-Type'] = 'application/json'
    }
    return config
  },
  (error) => Promise.reject(error),
)

// --------------------------------------------
// ROUTES EXEMPTÉES DU REDIRECT 401
// --------------------------------------------
// Sur ces routes, un 401 ne déclenche PAS de redirect vers /login. Le caller
// (composant ou contexte) gère l'erreur lui-même.
//
//   - Routes auth publiques (`/auth/*`) : 401 = identifiants invalides au
//     login / token expiré sur verify-email-reset, géré par le composant
//     (toast / écran d'erreur ciblé).
//   - `/me` : probe d'hydratation appelé au mount du `RecruiterAuthProvider`.
//     Un 401 ici = utilisateur pas encore authentifié, état initial normal
//     sur les pages publiques (signup/login/verify-email/forgot/reset). Le
//     contexte met silencieusement `recruiter=null` et `RecruiterProtectedRoute`
//     redirige vers /login UNIQUEMENT si la page nécessite une session. Sans
//     cette exemption, l'arrivée sur /verify-email après clic email envoyait
//     /me → 401 → redirect interceptor avant que le token soit consommé.
//   - `/auth/logout` : best-effort. 401 = cookie déjà invalide, logout réussi
//     côté front quand même. Pas la peine de spammer un message "session
//     expirée" à un user qui se déconnecte volontairement.
const RECRUITER_NO_REDIRECT_ROUTES = [
  '/recruiters/auth/signup',
  '/recruiters/auth/login',
  '/recruiters/auth/verify-email',
  '/recruiters/auth/forgot-password',
  '/recruiters/auth/reset-password',
  '/recruiters/auth/logout',
  '/recruiters/me',
]

const isNoRedirectRoute = (url) => {
  if (!url) return false
  return RECRUITER_NO_REDIRECT_ROUTES.some((route) => url.includes(route))
}

// --------------------------------------------
// HELPERS REDIRECT
// --------------------------------------------
const getCurrentPath = () => window.location.pathname + window.location.search

const safeRedirectToRecruiterLogin = (errorMessage) => {
  const targetUrl =
    '/recruteurs/login?error=unauthorized' +
    (errorMessage ? `&message=${encodeURIComponent(errorMessage)}` : '')

  if (getCurrentPath() === targetUrl) {
    return false
  }
  // Si déjà sur /recruteurs/login (sans erreur) → ne pas spam
  if (window.location.pathname === '/recruteurs/login') {
    return false
  }
  window.location.href = targetUrl
  return true
}

// --------------------------------------------
// RESPONSE INTERCEPTOR
// --------------------------------------------
recruiterAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error

    // Erreur réseau → laisser passer
    if (!response) return Promise.reject(error)

    const status = response.status
    const requestUrl = config?.url || ''
    const errorData = response.data

    // 413 : même traitement que sur `apiClient`. Le module Recruteur téléverse
    // un logo d'entreprise, il est donc exposé à la même limite serveur — et
    // son auth séparée ne change rien à la nature de l'erreur.
    const payloadError = toTypedHttpError(response)
    if (payloadError) {
      console.warn(`📦 Corps de requête refusé (413): ${requestUrl}`)
      return Promise.reject(payloadError)
    }

    // ═══════════════════════════════════════════════════════════
    // 404 RECRUITER_MODULE_DISABLED — laisser passer
    // Le composant affiche la page <RecruiterModuleDisabled />.
    // PAS de redirection (consigne user 2026-05-07).
    //
    // Le backend renvoie `{ error: 'Module non disponible...', code:
    // 'RECRUITER_MODULE_DISABLED' }`. On match UNIQUEMENT sur `code`
    // (canonique, plus robuste que `error` qui contient le message).
    // ═══════════════════════════════════════════════════════════
    const isModuleDisabled =
      status === 404 && errorData?.code === 'RECRUITER_MODULE_DISABLED'

    if (isModuleDisabled) {
      const moduleError = new Error(
        errorData?.message || 'Module recruteur non disponible pour cette communauté.',
      )
      moduleError.status = 404
      moduleError.code = 'RECRUITER_MODULE_DISABLED'
      moduleError.response = response
      return Promise.reject(moduleError)
    }

    // ═══════════════════════════════════════════════════════════
    // 401 UNAUTHORIZED
    // ═══════════════════════════════════════════════════════════
    if (status === 401) {
      // Routes exemptées (auth/*, /me, /auth/logout) : on ne redirige PAS,
      // le caller (composant / contexte) gère l'erreur localement.
      if (isNoRedirectRoute(requestUrl)) {
        // Erreur remonte normalement (refresh() le catch silencieusement
        // pour /me, les pages auth gèrent via err.code, etc.)
      } else {
        // Session recruteur invalide/expirée sur une route protégée
        // (jobs, applications, etc.) → redirect login recruteur
        const message =
          errorData?.message || 'Votre session recruteur a expiré. Veuillez vous reconnecter.'
        safeRedirectToRecruiterLogin(message)
      }
    }

    // ═══════════════════════════════════════════════════════════
    // Normalisation erreur pour tous les autres cas
    //
    // Le backend renvoie 2 shapes selon l'origine :
    //   - AppError.toJSON() → { error: { code, message, details } }
    //   - Erreurs ad-hoc     → { error: 'string' } ou { message: 'string' }
    //
    // On extrait UNIQUEMENT des strings pour éviter de coerce un objet en
    // "[object Object]" via new Error(obj). Ordre de priorité :
    //   1. errorData.message (string) — erreurs ad-hoc plates
    //   2. errorData.error (string)   — erreurs ad-hoc legacy
    //   3. errorData.error.message    — AppError.toJSON shape canonique
    //   4. error.message              — message axios (réseau, timeout)
    //   5. fallback HTTP_<status>
    // ═══════════════════════════════════════════════════════════
    const errorField = errorData?.error
    const serverMessage =
      (typeof errorData?.message === 'string' && errorData.message) ||
      (typeof errorField === 'string' && errorField) ||
      (typeof errorField?.message === 'string' && errorField.message) ||
      error.message ||
      `HTTP_${status}`

    const errorCode =
      errorData?.code ||
      errorField?.code ||
      (typeof errorField === 'string' ? errorField : null) ||
      `HTTP_${status}`

    const enhancedError = new Error(serverMessage)
    enhancedError.status = status
    enhancedError.code = errorCode
    enhancedError.details = errorField?.details || errorData?.details || null
    enhancedError.response = response
    enhancedError.originalError = error

    return Promise.reject(enhancedError)
  },
)

// --------------------------------------------
// SIMPLE WRAPPERS
// --------------------------------------------
export async function get(url, cfg = {}) {
  return (await recruiterAxios.get(url, cfg)).data
}
export async function post(url, data = {}, cfg = {}) {
  return (await recruiterAxios.post(url, data, cfg)).data
}
export async function put(url, data = {}, cfg = {}) {
  return (await recruiterAxios.put(url, data, cfg)).data
}
export async function del(url, cfg = {}) {
  return (await recruiterAxios.delete(url, cfg)).data
}
export async function patch(url, data = {}, cfg = {}) {
  return (await recruiterAxios.patch(url, data, cfg)).data
}

export function getRecruiterApiUrl() {
  return API_BASE_URL
}

export { recruiterAxios }

export default {
  get,
  post,
  put,
  delete: del,
  patch,
  getRecruiterApiUrl,
  axios: recruiterAxios,
}
